import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { MoveHorizontal, MoveVertical, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PAGE_WIDTH, PAGE_HEIGHT, PageGuide } from '@/types/yearbook';

const MM_TO_PT = 2.83465;
const PAGE_WIDTH_MM = Math.round(PAGE_WIDTH / MM_TO_PT);
const PAGE_HEIGHT_MM = Math.round(PAGE_HEIGHT / MM_TO_PT);

interface AddGuideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageWidth?: number;
  existingGuides?: PageGuide[];
  onAdd: (guide: { orientation: 'horizontal' | 'vertical'; position: number; applyToAll?: boolean }) => void;
}

const AddGuideDialog: React.FC<AddGuideDialogProps> = ({ open, onOpenChange, pageWidth, existingGuides = [], onAdd }) => {
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('vertical');
  const [edge, setEdge] = useState<'start' | 'end'>('start');
  const [valueMm, setValueMm] = useState<number>(20);
  const [applyToAll, setApplyToAll] = useState(false);

  const pageW = pageWidth ?? PAGE_WIDTH;
  const maxMm = orientation === 'vertical' ? Math.round(pageW / MM_TO_PT) : PAGE_HEIGHT_MM;

  const positionPt = useMemo(() => {
    const valuePt = Math.max(0, Math.min(valueMm, maxMm)) * MM_TO_PT;
    if (orientation === 'vertical') {
      return edge === 'start' ? valuePt : pageW - valuePt;
    }
    return edge === 'start' ? valuePt : PAGE_HEIGHT - valuePt;
  }, [valueMm, edge, orientation, maxMm, pageW]);

  const previewMaxW = 200;
  const previewMaxH = 290;
  const ratio = pageW / PAGE_HEIGHT;
  let previewW = previewMaxW;
  let previewH = previewW / ratio;
  if (previewH > previewMaxH) {
    previewH = previewMaxH;
    previewW = previewH * ratio;
  }
  const previewScale = previewW / pageW;
  const previewLeft = orientation === 'vertical' ? positionPt * previewScale : 0;
  const previewTop = orientation === 'horizontal' ? positionPt * previewScale : 0;

  const handleAdd = (keepOpen: boolean) => {
    if (valueMm < 0 || valueMm > maxMm) return;
    onAdd({ orientation, position: Math.round(positionPt * 100) / 100, applyToAll });
    if (keepOpen) {
      if (!applyToAll) toast.success('Repère ajouté', { duration: 1500 });
      setValueMm((v) => Math.min(maxMm, v + 10));
    } else {
      onOpenChange(false);
      setValueMm(20);
      setEdge('start');
    }
  };

  const startLabel = orientation === 'vertical' ? 'Gauche' : 'Haut';
  const endLabel = orientation === 'vertical' ? 'Droite' : 'Bas';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ajouter un repère</DialogTitle>
          <DialogDescription>
            Créez une règle d'aide à la mise en page. Les éléments s'aimanteront dessus.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4">
          <div className="flex-1 space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Orientation</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setOrientation('vertical'); setEdge('start'); }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-md border transition-colors ${
                    orientation === 'vertical'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted text-muted-foreground'
                  }`}
                >
                  <MoveVertical size={18} />
                  <span className="text-xs font-medium">Vertical</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setOrientation('horizontal'); setEdge('start'); }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-md border transition-colors ${
                    orientation === 'horizontal'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted text-muted-foreground'
                  }`}
                >
                  <MoveHorizontal size={18} />
                  <span className="text-xs font-medium">Horizontal</span>
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">À partir de</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEdge('start')}
                  className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                    edge === 'start'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted text-muted-foreground'
                  }`}
                >
                  {startLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setEdge('end')}
                  className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                    edge === 'end'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted text-muted-foreground'
                  }`}
                >
                  {endLabel}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Distance (mm) — max {maxMm}
              </label>
              <Input
                type="number"
                min={0}
                max={maxMm}
                step={1}
                value={valueMm}
                onChange={(e) => setValueMm(Math.max(0, Math.min(maxMm, parseInt(e.target.value || '0', 10))))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (e.shiftKey) handleAdd(true);
                    else handleAdd(false);
                  }
                }}
              />
            </div>

            <div className="flex items-start gap-2 rounded-md border border-border p-2.5">
              <Checkbox
                id="guide-apply-all"
                checked={applyToAll}
                onCheckedChange={(v) => setApplyToAll(v === true)}
                className="mt-0.5"
              />
              <label htmlFor="guide-apply-all" className="text-xs leading-snug cursor-pointer">
                <span className="font-medium">Appliquer à toutes les pages</span>
                <span className="block text-muted-foreground">
                  Le repère sera ajouté à l'ensemble des pages non verrouillées du livre.
                </span>
              </label>
            </div>
          </div>


          <div className="flex flex-col items-center justify-center">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Aperçu</div>
            <div
              className="relative bg-white border border-border rounded-sm shadow-sm overflow-hidden"
              style={{ width: previewW, height: previewH }}
            >
              {/* Existing guides (cyan) */}
              {existingGuides.map((g) => (
                g.orientation === 'vertical' ? (
                  <div
                    key={g.id}
                    className="absolute top-0 bottom-0"
                    style={{
                      left: g.position * previewScale,
                      width: 1,
                      borderLeft: '1px dashed hsl(190 80% 45%)',
                      opacity: 0.7,
                    }}
                  />
                ) : (
                  <div
                    key={g.id}
                    className="absolute left-0 right-0"
                    style={{
                      top: g.position * previewScale,
                      height: 1,
                      borderTop: '1px dashed hsl(190 80% 45%)',
                      opacity: 0.7,
                    }}
                  />
                )
              ))}
              {/* New guide preview (primary) */}
              {orientation === 'vertical' ? (
                <div
                  className="absolute top-0 bottom-0"
                  style={{
                    left: previewLeft,
                    width: 1,
                    borderLeft: '1.5px dashed hsl(var(--primary))',
                  }}
                />
              ) : (
                <div
                  className="absolute left-0 right-0"
                  style={{
                    top: previewTop,
                    height: 1,
                    borderTop: '1.5px dashed hsl(var(--primary))',
                  }}
                />
              )}
            </div>
            {existingGuides.length > 0 && (
              <div className="mt-2 text-[10px] text-muted-foreground text-center">
                {existingGuides.length} repère{existingGuides.length > 1 ? 's' : ''} déjà placé{existingGuides.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => handleAdd(true)} title="Ajouter et continuer (Shift+Entrée)">
              <Plus size={16} />
              Ajouter et continuer
            </Button>
            <Button onClick={() => handleAdd(false)} title="Ajouter et fermer (Entrée)">
              <Check size={16} />
              Ajouter et fermer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddGuideDialog;
